import { useState, useRef, useEffect } from 'react';
import { api, type StandWert, type Drueckteil } from '../lib/api';
import { moduleApi } from '../lib/moduleApi';
import { StatusBadge } from './StatusBadge';

interface Props {
  drueckteilId:    number;
  currentStandId:  number | null;
  currentStandWert:  string | null | undefined;
  currentStandFarbe: string | null | undefined;
  updatedAt:       string;
  standWerte:      StandWert[];
  onChanged?: (updated: Partial<Drueckteil>) => void;
  // Hangi modül endpoint'ine PATCH atılacak. Default: drueckteile
  modulePath?: 'drueckteile' | 'laufraeder' | 'baugruppen' | 'montage' | 'laser_kant';
  // Audit için acting user (werker abteilung user'ı)
  actingUserId?: number | null;
}

// Click-to-edit Stand-Zelle mit Inline-Speichern.
// Kompakte, professionelle Darstellung: Badge mit Farbpunkt, Hover-Ring, Dashed-Border im Leerzustand.
export function StandCell({
  drueckteilId, currentStandId, currentStandWert, currentStandFarbe,
  updatedAt, standWerte, onChanged, modulePath = 'drueckteile', actingUserId,
}: Props) {
  const updateFn = modulePath === 'drueckteile'
    ? (id: number, body: Partial<Drueckteil>) => api.drueckteile.update(id, body)
    : (id: number, body: Record<string, unknown>) =>
        moduleApi(modulePath).update(id, body as Partial<import('../lib/moduleApi').ModuleItem>);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState<string | null>(null);
  const [local,  setLocal] = useState<{ id: number | null; wert?: string | null; farbe?: string | null }>({
    id: currentStandId, wert: currentStandWert, farbe: currentStandFarbe,
  });
  const [lastUpdatedAt, setLastUpdatedAt] = useState(updatedAt);
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    setLocal({ id: currentStandId, wert: currentStandWert, farbe: currentStandFarbe });
    setLastUpdatedAt(updatedAt);
  }, [currentStandId, currentStandWert, currentStandFarbe, updatedAt]);

  useEffect(() => {
    if (editing && selectRef.current) selectRef.current.focus();
  }, [editing]);

  const save = async (newStandId: number | null) => {
    if (newStandId === local.id) { setEditing(false); return; }
    setSaving(true); setError(null);
    try {
      const res = await updateFn(drueckteilId, {
        stand_id: newStandId,
        updated_at: lastUpdatedAt,
        user_id: actingUserId ?? undefined,
      } as Partial<Drueckteil>);
      const s = standWerte.find(x => x.id === newStandId);
      setLocal({ id: newStandId, wert: s?.wert ?? null, farbe: s?.farbe ?? null });
      setLastUpdatedAt((res.data as { updated_at: string }).updated_at);
      setEditing(false);
      onChanged?.(res.data as Partial<Drueckteil>);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <select
        ref={selectRef}
        defaultValue={local.id ?? ''}
        disabled={saving}
        onBlur={e => save(e.target.value ? Number(e.target.value) : null)}
        onChange={e => save(e.target.value ? Number(e.target.value) : null)}
        onKeyDown={e => { if (e.key === 'Escape') { setEditing(false); setError(null); } }}
        className="text-xs border border-brand rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand/20 min-w-[140px]">
        <option value="">— keiner —</option>
        {standWerte.map(s => <option key={s.id} value={s.id}>{s.wert}</option>)}
      </select>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      disabled={saving}
      title="Klicken zum Ändern"
      className="group inline-flex items-center rounded-md transition focus:outline-none focus:ring-2 focus:ring-brand/30">
      {local.wert ? (
        <span className="hover:ring-2 hover:ring-brand/20 rounded-md transition">
          <StatusBadge label={local.wert} color={local.farbe ?? undefined} />
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-slate-400
                         border border-dashed border-slate-300 rounded-md
                         hover:border-brand hover:text-brand hover:bg-brand/5 transition">
          Stand wählen
        </span>
      )}
      {saving && <span className="ml-1 text-[10px] text-slate-400">…</span>}
      {error  && <span className="ml-1 text-[10px] text-red-600">{error}</span>}
    </button>
  );
}
